import React from 'react';
import { TrendingUp, Package, Star } from 'lucide-react';
import { useTranslation } from '../LanguageContext';
import { BASE_URL, EXTERNAL_BASE_URL } from '../apiConfig'

export default function TopSellingProducts({ data, loading }) {
  const { t } = useTranslation();

  const renderRank = (index) => {
    const num = index + 1;
    const colors = ['text-amber-500', 'text-gray-400', 'text-amber-700'];
    const color = colors[index] || '';
    if (index < 3) return (
      <div className="relative w-6 h-6 flex items-center justify-center">
        <Star className={`w-6 h-6 ${color} fill-current`} />
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">{num}</span>
      </div>
    );
    return (
      <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700">
        {num}
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white py-6 px-2 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
          <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">{t('dashboard.top_selling')}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.top_selling_desc')}</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3 py-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1">
                <div className="h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-2 w-1/4 bg-gray-100 dark:bg-gray-800 rounded mt-1" />
              </div>
              <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      ) : !data || data.length === 0 ? (
        <div className="pt-24 pb-28  flex flex-col items-center justify-center text-center">
          <Package className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-400">{t('dashboard.no_sales_data')}</p>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="grid grid-cols-[32px_1fr_auto] gap-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 pb-1">
            <span></span>
            <span>{t('dashboard.product')}</span>
            <div className="flex items-center gap-2 text-right">
              <span className="w-14">{t('dashboard.price_achat')}</span>
              <span className="w-14">{t('dashboard.price')}</span>
              <span className="w-14">{t('dashboard.marge')}</span>
              <span className="w-10">{t('dashboard.sold')}</span>
            </div>
          </div>
          {data.map((product, index) => (
            <div
              key={product.product_id}
              className="grid grid-cols-[32px_1fr_auto] gap-1 items-center px-2 py-1.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center justify-center w-4 h-6 shrink-0">
                {renderRank(index)}
              </div>

              <div className="flex items-center gap-1.5 min-w-0">
                {product.image ? (
                  <img
                    src={product.source_id ? `${EXTERNAL_BASE_URL}/storage/${product.image}` : `${BASE_URL}/storage/${product.image}`} 
                    alt={product.product_name}
                    className="w-10 h-10 rounded-lg object-cover bg-gray-100 dark:bg-gray-800 shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {product.product_name}
                </p>
              </div>

              <div className="flex items-center gap-1.5 text-right text-sm font-medium">
                <span className="w-14 text-gray-500 dark:text-gray-400">{product.price_achat.toFixed(2)}</span>
                <span className="w-14 text-gray-900 dark:text-gray-100">{product.price.toFixed(2)}</span>
                <span className={`w-14 font-semibold ${product.marge >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {product.marge >= 0 ? '+' : ''}{product.marge.toFixed(2)}
                </span>
                <span className="w-10 font-bold text-gray-900 dark:text-gray-100">{product.total_qty}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
